import { useState, useEffect, useRef } from "react";
import AudioEngine from "./audioEngine";

const SYS = "#38bdf8";
const SYS_DIM = "rgba(56,189,248,0.12)";
const SYS_BORDER = "rgba(56,189,248,0.25)";


const CAT = {
  work:     { color:"#3b82f6", bg:"rgba(59,130,246,0.10)",  icon:"💼", label:"Work" },
  personal: { color:"#a855f7", bg:"rgba(168,85,247,0.10)", icon:"🌱", label:"Personal" },
  health:   { color:"#22c55e", bg:"rgba(34,197,94,0.10)",  icon:"💪", label:"Health" },
  learning: { color:"#f59e0b", bg:"rgba(245,158,11,0.10)", icon:"📚", label:"Learning" },
  creative: { color:"#ec4899", bg:"rgba(236,72,153,0.10)", icon:"🎨", label:"Creative" },
  other:    { color:"#94a3b8", bg:"rgba(148,163,184,0.10)", icon:"⚡", label:"Other" },
};

const DEFAULT_XP = 10;

function xpPerLog(task){
  const weeklyXP = task.xp || DEFAULT_XP;
  const goals = task.goals || {};
  const weekly = goals.weekly;
  let goal = 0;
  if(weekly){
    if(task.metric === "time") goal = (parseInt(weekly.h||0)*60) + parseInt(weekly.m||0);
    else goal = parseFloat(weekly.amount||0);
  }
  if(!goal || goal <= 0) return weeklyXP;
  return weeklyXP / Math.max(1, goal);
}

// ─── Stats Charts ─────────────────────────────────────────────────────────────
// ─── Stats Charts ─────────────────────────────────────────────────────────────
const PERIOD_OPTIONS=[
  {id:"currentMonth", label:"THIS MONTH"},
  {id:"last30",       label:"LAST 30D"},
  {id:"last90",       label:"LAST 90D"},
  {id:"currentYear",  label:"THIS YEAR"},
  {id:"lifetime",     label:"LIFETIME"},
];

function getDateRange(period, userCreatedAt){
  const now=new Date();
  if(period==="currentMonth") return {from:new Date(now.getFullYear(),now.getMonth(),1),to:now};
  if(period==="last30")       return {from:new Date(now-30*86400000),to:now};
  if(period==="last90")       return {from:new Date(now-90*86400000),to:now};
  if(period==="currentYear")  return {from:new Date(now.getFullYear(),0,1),to:now};
  return {from:new Date(userCreatedAt||0),to:now};
}

function buildChartData(tasks, period, userCreatedAt){
  const {from,to}=getDateRange(period,userCreatedAt);
  return tasks.map(t=>{
    const logs=(t.logs||[]).filter(l=>{const d=new Date(l.date);return d>=from&&d<=to;});
    const xp=Math.round(logs.length*xpPerLog(t));
    const color=CAT[t.cat]?.color||SYS;
    return {id:t.id,title:t.title,cat:t.cat,color,logs:logs.length,xp};
  }).filter(d=>d.logs>0);
}

// Donut slice path builder
function donutSlices(values, cx, cy, r, inner){
  const total=values.reduce((s,v)=>s+v,0);
  if(!total) return [];
  let angle=-Math.PI/2;
  return values.map((v)=>{
    const sweep=(v/total)*2*Math.PI;
    const x1=cx+r*Math.cos(angle), y1=cy+r*Math.sin(angle);
    angle+=sweep;
    const x2=cx+r*Math.cos(angle), y2=cy+r*Math.sin(angle);
    const xi1=cx+inner*Math.cos(angle), yi1=cy+inner*Math.sin(angle);
    const xi2=cx+inner*Math.cos(angle-sweep), yi2=cy+inner*Math.sin(angle-sweep);
    const large=sweep>Math.PI?1:0;
    return `M${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2} L${xi1} ${yi1} A${inner} ${inner} 0 ${large} 0 ${xi2} ${yi2}Z`;
  });
}

function ChartCard({title, data, valueKey, centerLabel, chartType, animKey}){
  const [active,setActive]=useState(null);
  const [animated,setAnimated]=useState(false);

  useEffect(()=>{
    setAnimated(false);
    setActive(null);
    const t=setTimeout(()=>setAnimated(true),60);
    return()=>clearTimeout(t);
  },[animKey]);

  const values=data.map(d=>d[valueKey]);
  const total=values.reduce((s,v)=>s+v,0);
  const colors=data.map(d=>d.color);
  // eslint-disable-next-line no-unused-vars
  const labels=data.map(d=>d.title);
  const cx=90,cy=90,r=72,inner=42;
  const paths=donutSlices(values,cx,cy,r,inner);

  const activeItem=active!==null?data[active]:null;

  if(!total) return(
    <div style={{width:"82vw",maxWidth:300,flexShrink:0,scrollSnapAlign:"center",
      background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
      borderRadius:16,padding:"20px 16px",display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",minHeight:280}}>
      <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:2,marginBottom:16}}>{title}</div>
      <div style={{fontSize:36,marginBottom:10}}>📭</div>
      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",textAlign:"center",lineHeight:1.6}}>
        NO ACTIVITY<br/>IN THIS PERIOD
      </div>
    </div>
  );

  return(
    <div style={{width:"82vw",maxWidth:300,flexShrink:0,scrollSnapAlign:"center",
      background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
      borderRadius:16,padding:"16px",display:"flex",flexDirection:"column",gap:12,
      transition:"transform 0.3s ease, opacity 0.3s ease"}}>

      <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:2}}>{title}</div>

      {chartType==="pie"?(
        /* ── Donut ── */
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
          <div style={{position:"relative",width:180,height:180,
            opacity:animated?1:0,transform:animated?"scale(1)":"scale(0.85)",
            transition:"opacity 0.5s ease, transform 0.5s ease"}}>
            <svg width={180} height={180} style={{overflow:"visible"}}>
              <defs>
                <radialGradient id="pie_glass" cx="38%" cy="32%" r="62%" gradientUnits="objectBoundingBox">
                  <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.30"/>
                  <stop offset="40%"  stopColor="#ffffff" stopOpacity="0.08"/>
                  <stop offset="100%" stopColor="#000000" stopOpacity="0.20"/>
                </radialGradient>
                <radialGradient id="pie_inner_shadow" cx="62%" cy="68%" r="55%" gradientUnits="objectBoundingBox">
                  <stop offset="0%"   stopColor="#000000" stopOpacity="0.15"/>
                  <stop offset="100%" stopColor="#000000" stopOpacity="0"/>
                </radialGradient>
              </defs>
              {paths.map((d,i)=>{
                const isActive=active===i;
                const midAngle=-Math.PI/2+(values.slice(0,i).reduce((s,v)=>s+v,0)+values[i]/2)/total*2*Math.PI;
                const tx=isActive?Math.cos(midAngle)*4:0;
                const ty=isActive?Math.sin(midAngle)*4:0;
                return(
                  <g key={i} transform={`translate(${tx},${ty})`}
                    style={{cursor:"pointer",transition:"transform 0.25s"}}
                    onClick={()=>{AudioEngine.click();setActive(active===i?null:i)}}>
                    <path d={d} fill={colors[i]}
                      stroke="#000" strokeWidth={isActive?2.5:0.5}
                      opacity={active===null?0.9:isActive?1:0.25}
                      style={{transition:"opacity 0.25s"}}
                    />
                    <path d={d} fill="url(#pie_glass)"
                      opacity={active===null?1:isActive?1:0.25}
                      style={{pointerEvents:"none",transition:"opacity 0.25s"}}
                    />
                    <path d={d} fill="url(#pie_inner_shadow)"
                      opacity={active===null?1:isActive?1:0.25}
                      style={{pointerEvents:"none",transition:"opacity 0.25s"}}
                    />
                  </g>
                );
              })}
              {/* Center */}
              {activeItem?(
                <>
                  <text x={cx} y={cy-8} textAnchor="middle" fill={activeItem.color}
                    style={{fontFamily:"'Share Tech Mono',monospace",fontSize:15,fontWeight:800}}>
                    {activeItem[valueKey]}
                  </text>
                  <text x={cx} y={cy+8} textAnchor="middle" fill="#aaa"
                    style={{fontFamily:"monospace",fontSize:8,letterSpacing:1}}>{centerLabel}</text>
                  <text x={cx} y={cy+22} textAnchor="middle" fill="#667"
                    style={{fontFamily:"monospace",fontSize:7}}>
                    {activeItem.title.length>12?activeItem.title.slice(0,11)+"…":activeItem.title}
                  </text>
                </>
              ):(
                <>
                  <text x={cx} y={cy-6} textAnchor="middle" fill={SYS}
                    style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,fontWeight:800}}>{total}</text>
                  <text x={cx} y={cy+12} textAnchor="middle" fill="#64748b"
                    style={{fontFamily:"monospace",fontSize:8,letterSpacing:1}}>{centerLabel}</text>
                </>
              )}
            </svg>
          </div>
          {/* Legend */}
          <div style={{width:"100%",display:"flex",flexDirection:"column",gap:5}}>
            {data.map((d,i)=>(
              <div key={i} onClick={()=>{AudioEngine.click();setActive(active===i?null:i)}}
                style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",
                  padding:"4px 6px",borderRadius:6,
                  background:active===i?`${d.color}15`:"transparent",
                  transition:"background 0.2s"}}>
                <div style={{width:8,height:8,borderRadius:2,background:d.color,flexShrink:0}}/>
                <div style={{fontFamily:"monospace",fontSize:9,color:active===i?d.color:"#aaa",
                  flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.title}</div>
                <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                  color:d.color,fontWeight:700}}>{d[valueKey]}</div>
              </div>
            ))}
          </div>
        </div>
      ):(
        /* ── Bar ── */
        <div style={{display:"flex",flexDirection:"column",gap:7,
          opacity:animated?1:0,transform:animated?"translateY(0)":"translateY(12px)",
          transition:"opacity 0.4s ease, transform 0.4s ease"}}>
          {data.map((d,i)=>{
            const max=Math.max(...values,1);
            const pct=(d[valueKey]/max)*100;
            const isActive=active===i;
            return(
              <div key={i} onClick={()=>{AudioEngine.click();setActive(active===i?null:i)}}
                style={{cursor:"pointer",padding:"4px 0",borderRadius:6,
                  background:isActive?`${d.color}10`:"transparent",transition:"background 0.2s"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <div style={{fontFamily:"monospace",fontSize:9,
                    color:isActive?d.color:"#667",minWidth:70,maxWidth:70,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.title}</div>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                    color:d.color,fontWeight:700,marginLeft:"auto"}}>{d[valueKey]}</div>
                </div>
                <div style={{height:8,background:"rgba(255,255,255,0.04)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:4,background:d.color,
                    width:animated?`${pct}%`:"0%",
                    opacity:active===null?1:isActive?1:0.25,
                    transition:`width 0.6s ease ${i*0.05}s, opacity 0.25s`}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Activity Heatmap ─────────────────────────────────────────────────────────
const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const WEEKDAYS = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
const WEEKDAY_FULL = ["Sundays","Mondays","Tuesdays","Wednesdays","Thursdays","Fridays","Saturdays"];

function getGreenShade(count, max){
  if(!count) return "rgba(255,255,255,0.06)";
  if(max <= 0) return "#bbf7d0";
  const ratio = Math.min(count / max, 1);
  if(ratio <= 0.25) return "#bbf7d0";
  if(ratio <= 0.5)  return "#4ade80";
  if(ratio <= 0.75) return "#16a34a";
  return "#15803d";
}

function getShadeForSelected(logged, total){
  if(!total || !logged) return "rgba(255,255,255,0.06)";
  const ratio = logged / total;
  if(ratio <= 0.25) return "#bbf7d0";
  if(ratio <= 0.5)  return "#4ade80";
  if(ratio <= 0.75) return "#16a34a";
  return "#15803d";
}

function ActivityHeatmap({ tasks }){
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const scrollRef = useRef(null);
  const activeCardRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(now.getMonth());

  // Scroll to current month on mount
  useEffect(()=>{
    if(activeCardRef.current && scrollRef.current){
      const container = scrollRef.current;
      const el = activeCardRef.current;
      const offset = el.offsetLeft - container.offsetWidth/2 + el.offsetWidth/2;
      container.scrollTo({left: offset, behavior:"smooth"});
    }
  },[]);

  // Track active card on scroll
  function onScroll(){
    const el = scrollRef.current; if(!el) return;
    const cards = [...el.children];
    const cc = el.getBoundingClientRect().left + el.offsetWidth/2;
    let closest=0, dist=Infinity;
    cards.forEach((card,i)=>{
      const d = Math.abs(cc-(card.getBoundingClientRect().left+card.offsetWidth/2));
      if(d<dist){dist=d;closest=i;}
    });
    setActiveIdx(closest);
  }

  useEffect(()=>{
    const el=scrollRef.current; if(!el) return;
    el.addEventListener("scroll",onScroll,{passive:true});
    return()=>el.removeEventListener("scroll",onScroll);
  },[]);

  const isFiltered = selectedTargets.length > 0;
  const activeTasks = isFiltered ? tasks.filter(t=>selectedTargets.includes(t.id)) : tasks;

  // Build dayMap per month
  function buildDayMap(monthIdx){
    const map = {};
    activeTasks.forEach(t=>{
      (t.logs||[]).forEach(l=>{
        if(l.status==="In Progress"||l.status==="Not Started") return;
        const d=new Date(l.date);
        if(d.getFullYear()===year && d.getMonth()===monthIdx){
          const day=d.getDate();
          map[day]=(map[day]||0)+1;
        }
      });
    });
    return map;
  }

  // Insights based on active card month
  const activeMonth = activeIdx;
  let mostDisciplined=null, mostActiveDay=null;
  if(!isFiltered){
    const counts=tasks.map(t=>({
      title:t.title,
      count:(t.logs||[]).filter(l=>{
        const d=new Date(l.date);
        return d.getFullYear()===year&&d.getMonth()===activeMonth
          &&l.status!=="In Progress"&&l.status!=="Not Started";
      }).length
    })).filter(t=>t.count>0).sort((a,b)=>b.count-a.count);
    if(counts.length) mostDisciplined=counts[0].title;
    const wdCount=[0,0,0,0,0,0,0];
    tasks.forEach(t=>{
      (t.logs||[]).forEach(l=>{
        const d=new Date(l.date);
        if(d.getFullYear()===year&&d.getMonth()===activeMonth
          &&l.status!=="In Progress"&&l.status!=="Not Started")
          wdCount[d.getDay()]++;
      });
    });
    const maxWd=Math.max(...wdCount);
    if(maxWd>0) mostActiveDay=WEEKDAY_FULL[wdCount.indexOf(maxWd)];
  }

  const BOX=32, GAP=4;

  return(
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"0 18px",marginBottom:12}}>
        <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2}}>[ ACTIVITY ]</div>
        <select value={year} onChange={e=>{AudioEngine.click();setYear(parseInt(e.target.value))}}
          style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
            borderRadius:6,color:"#94a3b8",fontFamily:"monospace",fontSize:10,
            padding:"3px 8px",cursor:"pointer",outline:"none"}}>
          {Array.from({length:4},(_,i)=>currentYear-i).map(y=>(
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Target filter pills */}
      <div style={{display:"flex",flexDirection:"row",gap:6,
        overflowX:"scroll",WebkitOverflowScrolling:"touch",
        padding:"0 18px 12px",msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {tasks.map(t=>{
          const sel=selectedTargets.includes(t.id);
          const col=CAT[t.cat]?.color||SYS;
          return(
            <button key={t.id}
              onClick={()=>{AudioEngine.click();setSelectedTargets(prev=>
                prev.includes(t.id)?prev.filter(x=>x!==t.id):[...prev,t.id]);}}
              style={{padding:"4px 10px",borderRadius:20,flexShrink:0,border:"none",
                outline:`1px solid ${sel?col+"60":"rgba(255,255,255,0.06)"}`,
                background:sel?col+"18":"rgba(255,255,255,0.02)",
                color:sel?col:"#64748b",fontFamily:"monospace",fontSize:9,
                cursor:"pointer",whiteSpace:"nowrap",letterSpacing:1,transition:"all 0.2s"}}>
              {CAT[t.cat]?.icon} {t.title}
            </button>
          );
        })}
      </div>

      {/* Month cards — horizontally scrollable */}
      <div ref={scrollRef} onScroll={onScroll}
        style={{display:"flex",flexDirection:"row",gap:14,
          overflowX:"scroll",WebkitOverflowScrolling:"touch",
          paddingLeft:18,paddingRight:18,paddingBottom:8,
          scrollSnapType:"x mandatory",
          msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {MONTHS.map((monthName, monthIdx)=>{
          const isActive = monthIdx===activeIdx;
          const dayMap = buildDayMap(monthIdx);
          const maxCount = Math.max(...Object.values(dayMap),1);
          const daysInMonth = new Date(year, monthIdx+1, 0).getDate();
          const firstDOW = new Date(year, monthIdx, 1).getDay();
          const cells=[];
          for(let i=0;i<firstDOW;i++) cells.push(null);
          for(let d=1;d<=daysInMonth;d++) cells.push(d);

          return(
            <div key={monthIdx}
              ref={monthIdx===now.getMonth()&&year===currentYear?activeCardRef:null}
              style={{
                flexShrink:0,
                scrollSnapAlign:"center",
                transform:isActive?"scale(1.04)":"scale(0.92)",
                opacity:isActive?1:0.5,
                transition:"transform 0.35s ease,opacity 0.35s ease",
                background:"rgba(4,12,28,0.9)",
                border:`1px solid ${isActive?"rgba(74,222,128,0.3)":"rgba(56,189,248,0.1)"}`,
                borderRadius:14,
                padding:"14px 14px 16px",
                boxShadow:isActive?"0 16px 48px rgba(74,222,128,0.08)":"none",
              }}>
              {/* Month name */}
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
                color:"#4ade80",letterSpacing:3,marginBottom:10,fontWeight:700}}>
                {monthName} {year}
              </div>
              {/* Weekday headers */}
              <div style={{display:"grid",gridTemplateColumns:`repeat(7,${BOX}px)`,gap:GAP,marginBottom:4}}>
                {WEEKDAYS.map(d=>(
                  <div key={d} style={{fontFamily:"monospace",fontSize:7,color:"#334155",
                    textAlign:"center",letterSpacing:0}}>{d}</div>
                ))}
              </div>
              {/* Day boxes */}
              <div style={{display:"grid",gridTemplateColumns:`repeat(7,${BOX}px)`,gap:GAP}}>
                {cells.map((day,i)=>{
                  if(day===null) return <div key={`e${i}`} style={{width:BOX,height:BOX}}/>;
                  const count=dayMap[day]||0;
                  const bg=isFiltered
                    ?getShadeForSelected(count,selectedTargets.length)
                    :getGreenShade(count,maxCount);
                  const isToday=year===now.getFullYear()
                    &&monthIdx===now.getMonth()&&day===now.getDate();
                  return(
                    <div key={day} style={{
                      width:BOX,height:BOX,borderRadius:5,background:bg,
                      border:isToday?"1px solid rgba(74,222,128,0.7)":"1px solid transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      flexDirection:"column",gap:1}}>
                      <span style={{fontFamily:"monospace",fontSize:7,
                        color:count?"rgba(0,0,0,0.55)":"#334155",
                        fontWeight:count?700:400}}>{day}</span>
                      {count>0&&(
                        <span style={{fontFamily:"monospace",fontSize:6,
                          color:"rgba(0,0,0,0.4)",fontWeight:700}}>{count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div style={{display:"flex",gap:4,justifyContent:"center",marginTop:8,marginBottom:4}}>
        {MONTHS.map((_,i)=>(
          <div key={i} style={{
            width:i===activeIdx?14:4,height:4,borderRadius:2,
            background:i===activeIdx?"#4ade80":"rgba(255,255,255,0.08)",
            transition:"all 0.3s ease"}}/>
        ))}
      </div>

      {/* Insight messages */}
      {(mostDisciplined||mostActiveDay)&&(
        <div style={{padding:"8px 18px 0",display:"flex",flexDirection:"column",gap:5}}>
          {mostDisciplined&&(
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
              color:"#4ade80",letterSpacing:1}}>
              ⚡ <strong>{mostDisciplined}</strong> is your most disciplined target
            </div>
          )}
          {mostActiveDay&&(
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
              color:"#38bdf8",letterSpacing:1}}>
              📅 Most active on <strong>{mostActiveDay}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function StatsCharts({tasks, userCreatedAt}){
  const [chartType,setChartType]=useState("pie");
  const [period,setPeriod]=useState("currentMonth");
  const scrollRef=useRef(null);
  const [activeIdx,setActiveIdx]=useState(0);

  const data=buildChartData(tasks,period,userCreatedAt);
  const totalLogs=data.reduce((s,d)=>s+d.logs,0);
  const totalXP=data.reduce((s,d)=>s+d.xp,0);
  const animKey=`${period}-${chartType}`;

  // Zoom on scroll (same pattern as CardScroller)
  function onScroll(){
    const el=scrollRef.current; if(!el)return;
    const cards=[...el.children];
    const cc=el.getBoundingClientRect().left+el.offsetWidth/2;
    let closest=0,dist=Infinity;
    cards.forEach((card,i)=>{
      const d=Math.abs(cc-(card.getBoundingClientRect().left+card.offsetWidth/2));
      if(d<dist){dist=d;closest=i;}
    });
    setActiveIdx(closest);
  }

  useEffect(()=>{
    const el=scrollRef.current; if(!el)return;
    onScroll();
    el.addEventListener("scroll",onScroll,{passive:true});
    return()=>el.removeEventListener("scroll",onScroll);
  },[data.length]);

  const chartDefs=[
    {key:"logs", title:"LOGS PER TARGET", centerLabel:"LOGS"},
    {key:"xp",   title:"XP PER TARGET",   centerLabel:"XP"},
  ];

  return(
    <div style={{padding:"0 0 32px"}}>
      {/* Activity Heatmap */}
      <ActivityHeatmap tasks={tasks}/>
      <div style={{height:1,background:"rgba(255,255,255,0.05)",margin:"8px 18px 16px"}}/>
      {/* Header row */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"0 18px",marginBottom:12}}>
        <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2}}>[ STATS ]</div>
        <div style={{display:"flex",gap:4,background:"rgba(255,255,255,0.03)",
          borderRadius:8,padding:3,border:"1px solid rgba(255,255,255,0.06)"}}>
          {[{id:"pie",icon:"◉"},{id:"bar",icon:"▦"}].map(({id,icon})=>(
            <button key={id} onClick={()=>{AudioEngine.click();setChartType(id)}}
              style={{padding:"4px 12px",borderRadius:6,border:"none",cursor:"pointer",
                background:chartType===id?SYS_DIM:"transparent",
                color:chartType===id?SYS:"#64748b",fontFamily:"monospace",fontSize:11,
                transition:"all 0.2s"}}>
              {icon} {id.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Summary line */}
      <div style={{padding:"0 18px",marginBottom:12}}>
        <div style={{background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
          borderRadius:10,padding:"10px 14px",display:"flex",gap:20,alignItems:"center"}}>
          <div>
            <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,
              fontWeight:800,color:SYS}}>{totalLogs}</span>
            <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b",
              marginLeft:6,letterSpacing:1}}>LOGS</span>
          </div>
          <div style={{width:1,height:24,background:"rgba(255,255,255,0.06)"}}/>
          <div>
            <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,
              fontWeight:800,color:"#818cf8"}}>{totalXP}</span>
            <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b",
              marginLeft:6,letterSpacing:1}}>XP</span>
          </div>
          <div style={{marginLeft:"auto",fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
            {PERIOD_OPTIONS.find(p=>p.id===period)?.label}
          </div>
        </div>
      </div>

      {/* Period pills */}
      <div style={{display:"flex",gap:6,overflowX:"auto",padding:"0 18px 12px",
        msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {PERIOD_OPTIONS.map(p=>(
          <button key={p.id} onClick={()=>{AudioEngine.click();setPeriod(p.id)}}
            style={{padding:"5px 12px",borderRadius:20,flexShrink:0,
              border:`1px solid ${period===p.id?SYS_BORDER:"rgba(255,255,255,0.06)"}`,
              background:period===p.id?SYS_DIM:"rgba(255,255,255,0.02)",
              color:period===p.id?SYS:"#64748b",fontFamily:"monospace",fontSize:9,
              cursor:"pointer",whiteSpace:"nowrap",letterSpacing:1,transition:"all 0.2s"}}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Scrollable chart cards */}
      <div ref={scrollRef}
        onScroll={onScroll}
        style={{display:"flex",gap:14,overflowX:"auto",
          paddingLeft:18,paddingRight:18,paddingBottom:8,
          scrollSnapType:"x mandatory",WebkitOverflowScrolling:"touch",
          msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {chartDefs.map(({key,title,centerLabel},idx)=>{
          const isActive=idx===activeIdx;
          return(
            <div key={key} style={{
              transform:isActive?"scale(1.04)":"scale(0.91)",
              opacity:isActive?1:0.5,
              transition:"transform 0.35s ease, opacity 0.35s ease",
              flexShrink:0,
            }}>
              <ChartCard
                title={title}
                data={data}
                valueKey={key}
                centerLabel={centerLabel}
                chartType={chartType}
                animKey={animKey}
              />
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div style={{display:"flex",gap:5,justifyContent:"center",marginTop:10}}>
        {chartDefs.map((_,i)=>(
          <div key={i} style={{width:i===activeIdx?16:5,height:5,borderRadius:3,
            background:i===activeIdx?SYS:"rgba(255,255,255,0.1)",
            transition:"all 0.3s ease"}}/>
        ))}
      </div>
    </div>
  );
}


